/**
 * Client-side mirror of the console-api registry module
 * (`infra/lib/lambdas/console-api/registry.ts`). The shape and the
 * `clientValidate` logic are ported verbatim so the SPA's pre-flight
 * validation produces the SAME messages the server-authoritative
 * `validateValue` would return — the UI can show a rejection before the
 * round-trip without the two ever disagreeing.
 *
 * Source of truth is `config/settings-registry.yaml`; the build's
 * `gen:registry` prebuild renders it into `../generated/registry.json`
 * (regenerated before every test/build), which this module imports.
 */

import settingsJson from '../generated/registry.json';

/** Mirrors the Lambda's SettingEntry (registry.ts lines 4-23) field-for-field. */
export interface SettingEntry {
  key: string;
  title: string;
  description: string;
  type:
    | 'number' | 'integer' | 'boolean' | 'string' | 'enum'
    | 'list' | 'table' | 'markdown' | 'json';
  unit?: string;
  default?: unknown;
  constraints?: Record<string, unknown>;
  enforcement: 'hard' | 'soft' | 'advisory' | 'infra';
  store: string;
  fallback?: string;
  read_path: string;
  change: string;
  dashboard: 'editable' | 'read-only' | 'hidden';
  per_org_override?: string;
  secret?: boolean;
  framework_ref?: string;
}

export const REGISTRY: SettingEntry[] = settingsJson as SettingEntry[];

/** The set of `type`s a SettingControl knows how to render. */
export const CONTROL_TYPES: ReadonlySet<SettingEntry['type']> = new Set([
  'number', 'integer', 'boolean', 'string', 'enum',
  'list', 'table', 'markdown', 'json',
]);

export function byKey(key: string): SettingEntry | undefined {
  return REGISTRY.find((e) => e.key === key);
}

/**
 * Exhaustiveness guard: if a 10th member is ever added to
 * `SettingEntry['type']`, every switch that calls this in its default branch
 * stops compiling — forcing the new case to be handled instead of silently
 * falling through to a "valid"/empty result.
 */
export function assertNever(x: never): never {
  throw new Error(`Unhandled SettingEntry type: ${String(x)}`);
}

/**
 * The 13 prefix→title category pairs, copied verbatim from
 * `agent/scripts/render_settings_doc.py` `_CATEGORIES` (actual path:
 * `agent/src/penseur_trading/admin/render_settings_doc.py`). Settings cards
 * group identically to docs/configuration.md, in this exact order. First
 * matching prefix wins — note `org.infra.` precedes `org.` so it claims its
 * entries before the broader `org.` bucket.
 */
export const CATEGORY_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['risk.hard.', 'Risk — hard limits'],
  ['risk.soft.', 'Risk — soft limits'],
  ['risk.intraday.', 'Risk — intraday passes'],
  ['strategies.', 'Strategy allow-lists'],
  ['sectors.', 'Sector map'],
  ['research.', 'Research / universe'],
  ['context.', 'Org context artifacts'],
  ['org.infra.', 'Per-org infrastructure'],
  ['org.', 'Per-org behavior (SSM)'],
  ['platform.', 'Platform parameters'],
  ['env.', 'Deployment environment variables'],
  ['macro.', 'Macro calendar'],
  ['code.', 'Code-default knobs'],
];

export interface Category {
  prefix: string;
  title: string;
  entries: SettingEntry[];
}

/**
 * Group the registry into the 13 categories, registry (= YAML) order preserved
 * within each. Categories with no entries are omitted, matching the doc
 * renderer's "if not entries: continue". The doc renderer asserts no entry is
 * uncategorized; every current key matches one of the 13 prefixes.
 */
export function categories(): Category[] {
  const groups = new Map<string, SettingEntry[]>();
  for (const [prefix] of CATEGORY_PAIRS) groups.set(prefix, []);
  for (const entry of REGISTRY) {
    const match = CATEGORY_PAIRS.find(([prefix]) => entry.key.startsWith(prefix));
    if (match) groups.get(match[0])!.push(entry);
  }
  return CATEGORY_PAIRS
    .map(([prefix, title]) => ({ prefix, title, entries: groups.get(prefix)! }))
    .filter((c) => c.entries.length > 0);
}

/**
 * Port of the Lambda's `validateValue` switch (writes.ts imports it from
 * registry.ts lines 44-81). Messages are kept IDENTICAL to the server so the
 * client and server agree. Returns null when valid, else the rejection reason.
 */
export function clientValidate(entry: SettingEntry, value: unknown): string | null {
  const c = entry.constraints ?? {};
  switch (entry.type) {
    case 'number':
    case 'integer': {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return `${entry.key}: expected a number`;
      }
      if (entry.type === 'integer' && !Number.isInteger(value)) {
        return `${entry.key}: expected an integer`;
      }
      if (typeof c.min === 'number' && value < c.min) return `${entry.key}: below min ${c.min}`;
      if (typeof c.max === 'number' && value > c.max) return `${entry.key}: above max ${c.max}`;
      return null;
    }
    case 'boolean':
      return typeof value === 'boolean' ? null : `${entry.key}: expected a boolean`;
    case 'enum': {
      const values = (c.values as unknown[]) ?? [];
      return values.includes(value)
        ? null
        : `${entry.key}: must be one of ${JSON.stringify(values)}`;
    }
    case 'string':
    case 'markdown':
      return typeof value === 'string' ? null : `${entry.key}: expected a string`;
    case 'list':
      return Array.isArray(value) ? null : `${entry.key}: expected a list`;
    case 'table':
      return Array.isArray(value) || (typeof value === 'object' && value !== null)
        ? null
        : `${entry.key}: expected a table (list or object)`;
    case 'json':
      return typeof value === 'object' && value !== null
        ? null
        : `${entry.key}: expected a JSON object`;
    default:
      return assertNever(entry.type);
  }
}
